import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';

const STEPS = ['step1', 'step2', 'step3', 'step4'] as const;

const ProcessSection: React.FC = () => {
  const { t } = useLanguage();
  const ref = useRef<HTMLElement | null>(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });

  return (
    <section
      id="process"
      ref={ref}
      className="relative py-24 md:py-32 bg-background"
      aria-labelledby="process-heading"
    >
      <div className="pointer-events-none absolute inset-0 grid-lines opacity-[0.05]" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header — full bleed editorial */}
        <motion.div
          className="mb-16 md:mb-20"
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
          transition={{ duration: 0.8 }}
        >
          <span className="eyebrow">— {t('process.eyebrow')}</span>
          <h2
            id="process-heading"
            className="mt-4 font-display text-foreground text-[clamp(2rem,4.2vw,3.75rem)] leading-[1.04] tracking-tight max-w-3xl"
          >
            {t('process.title')}
          </h2>
          <p className="mt-5 text-muted-foreground text-base md:text-lg max-w-2xl">
            {t('process.subtitle')}
          </p>
        </motion.div>

        {/* Vertical step rail */}
        <ol className="relative space-y-px border border-border/60 rounded-2xl overflow-hidden bg-card shadow-card">
          {STEPS.map((stepKey, idx) => (
            <motion.li
              key={stepKey}
              className="group relative grid grid-cols-12 gap-4 p-5 sm:p-8 md:p-12 bg-background/40 hover:bg-card/70 transition-colors duration-500"
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
              transition={{ duration: 0.6, delay: idx * 0.12 }}
            >
              {/* Step number */}
              <div className="col-span-3 md:col-span-3 lg:col-span-2">
                <div className="font-display text-4xl md:text-6xl text-lime/80 leading-none group-hover:text-lime transition-colors">
                  {t(`process.steps.${stepKey}.num`)}
                </div>
              </div>

              {/* Content */}
              <div className="col-span-9 md:col-span-8 lg:col-span-9 md:col-start-4 lg:col-start-3 flex flex-col md:flex-row md:items-baseline gap-2 md:gap-12">
                <h3 className="font-display text-foreground text-xl md:text-3xl leading-tight tracking-tight md:flex-shrink-0 md:basis-72">
                  {t(`process.steps.${stepKey}.title`)}
                </h3>
                <p className="text-muted-foreground text-sm md:text-lg leading-relaxed">
                  {t(`process.steps.${stepKey}.desc`)}
                </p>
              </div>

              {/* Hover accent line */}
              <div
                className="absolute left-0 top-0 h-full w-[3px] bg-lime origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500"
                aria-hidden="true"
              />
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default ProcessSection;
